import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '~/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  cn(
    'button relative inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-color duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        default: 'button--default',
        destructive:
          'button--destructive bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'destructive-outline':
          'button--destructive-outline border-[1.5px] border-destructive text-destructive hover:bg-destructive/5 focus-visible:bg-destructive/5',
        'info-outline':
          'button--info-outline border-[1.5px] border-info text-info hover:bg-info/5 focus-visible:bg-info/5',
        'success-outline':
          'button--success-outline border-[1.5px] border-success text-success hover:bg-success/5 focus-visible:bg-success/5',
        outline: 'button--outline',
        secondary:
          'button--secondary bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'button--ghost hover:bg-accent hover:text-accent-foreground',
        link: 'button--link text-black underline-offset-4 hover:underline',
        custom: cn(
          'button--custom text-[var(--cta-foreground-custom)] bg-[var(--cta-background-custom)] border-[var(--cta-border-custom)]',
          'hover:text-[var(--cta-foreground-hover-custom)] hover:bg-[var(--cta-background-hover-custom)] hover:border-[var(--cta-border-hover-custom)]',
          'focus-visible:text-[var(--cta-foreground-hover-custom)] focus-visible:bg-[var(--cta-background-hover-custom)] focus-visible:border-[var(--cta-border-hover-custom)]',
        ),
      },
      size: {
        default: 'button--size-default h-10 px-4 py-2',
        sm: 'button--size-sm h-9 rounded-md px-3',
        lg: 'button--size-lg h-11 rounded-md px-8',
        custom: '',
        icon: 'button--size-icon h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const loaderVariants = cva('button__loader aspect-square h-3/4 animate-spin', {
  variants: {
    variant: {
      default: 'text-primary-foreground',
      destructive: 'text-destructive-foreground',
      'destructive-outline': 'text-destructive-foreground',
      'info-outline': 'text-info',
      'success-outline': 'text-success',
      outline: 'text-foreground',
      secondary: 'text-secondary-foreground',
      ghost: 'text-foreground',
      link: 'text-primary',
      custom: '', // TODO create specific CSS variables that will take the button colours
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loader?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loader = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        data-button-variant={variant}
        className={cn(
          buttonVariants({ variant, size, className }),
          loader && 'text-transparent',
        )}
        ref={ref}
        {...props}
      >
        {loader && (
          <div
            aria-hidden
            className={cn(
              'button__loader',
              'absolute inset-0 flex items-center justify-center transition-opacity',
              loader ? 'opacity-100' : 'opacity-0',
            )}
          >
            <Loader2 className={loaderVariants({ variant })} />
          </div>
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
